import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { findExactSkuProductUrl } from '@theme/sku-search';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  /** @type {boolean} */
  #commitInProgress = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.searchPageInput.form?.addEventListener('submit', this.#handleFormSubmit);
  }

  disconnectedCallback() {
    this.refs.searchPageInput.form?.removeEventListener('submit', this.#handleFormSubmit);
    super.disconnectedCallback();
  }

  /**
   * @param {SubmitEvent} event
   */
  #handleFormSubmit = (event) => {
    event.preventDefault();
    void this.#handleSearchCommit();
  };

  #handleSearchCommit = async () => {
    if (this.#commitInProgress) return;

    const form = this.refs.searchPageInput.form;
    const query = this.refs.searchPageInput.value.trim();

    if (!form) return;

    if (!query) {
      HTMLFormElement.prototype.submit.call(form);
      return;
    }

    this.#commitInProgress = true;

    try {
      const skuUrl = await findExactSkuProductUrl(query);

      if (skuUrl) {
        window.location.replace(skuUrl);
        return;
      }

      HTMLFormElement.prototype.submit.call(form);
    } finally {
      this.#commitInProgress = false;
    }
  };

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
